import CryptoJS from "crypto-js";
import { t } from "@lingui/core/macro";
const url_backend = process.env.REACT_APP_URL_BACKEND;

/**
 * A function to decode base64 data
 * @param {string} encodeData - Sign in data
 * @returns {object}  Return a position object
 */
export const decrypt = (encodedData) => {
  if (encodedData) {
    try {
      // (decoding Base64)
      const decodedString = atob(encodedData); // 'atob' decodes a Base64 string
      const parsedData = JSON.parse(decodedString);

      if (
        typeof parsedData.lat === "number" &&
        typeof parsedData.lng === "number"
      ) {
        return parsedData;
      } else {
        throw new Error("Decoded data not valid.");
      }
    } catch (e) {
      console.error("Error in decoding process:", e);
      // return default position
      return { lat: 39.23297355676132, lng: 9.302428439198792 };
    }
  }
};

/**
 * A function to encode base64 data
 * @param {string} latitude - Latitude position
 * @param {string} longitude - Longitude position
 * @returns {object}  Return an encoded position object
 */
export const encryptPosition = (latitude, longitude) => {
  if (!latitude || !longitude) {
    return;
  }

  const data = { lat: parseFloat(latitude), lng: parseFloat(longitude) };
  const jsonString = JSON.stringify(data);

  // (encoding Base64)
  const encodedData = btoa(jsonString); // 'btoa' encodes a string to Base64
  return encodedData;
};

/**
 * A function to encrypt a story object
 * @param {string} id - Story unique identifier
 * @param {string} latitude - Latitude position
 * @param {string} longitude - Longitude position
 * @returns {object}  Return an encoded story object
 */
export const encryptStory = (id, latitude, longitude) => {
  if (!id || !latitude || !longitude) {
    return;
  }

  const data = {
    id: id,
    lat: parseFloat(latitude),
    lng: parseFloat(longitude),
  };
  const jsonString = JSON.stringify(data);

  // (encoding Base64)
  const encodedData = btoa(jsonString); // 'btoa' encodes a string to Base64
  return encodedData;
};

/**
 * A function to parse a local storage object
 * @returns {object}  Return a json representation of a local storage
 */
export const isUserLoggedIn = () => {
  return JSON.parse(localStorage.getItem("isAuthenticated"));
};

/**
 * A function to debounce a function passed by argument
 * @param {function} func - Function to debounce
 * @param {number} delay - Delay value
 * @returns {object}  Return a set timeout function
 */
export const debounce = (func, delay) => {
  let debounceTimer;
  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func.apply(context, args), delay);
  };
};

/**
 * A function to convert lat/long data in w3w
 * @param {string} lat - Latitude position
 * @param {string} lng - Longitude position
 * @param {string} lang - Language used for w3w conversion
 * @returns {object}  Return a w3w object
 */
export const getW3wInfoByCoordinates = async (lat, lng, lang = "it") => {
  const response = await window.what3words.api.convertTo3wa({
    coordinates: { lat, lng },
    language: lang,
    format: "json",
  });

  return response;
};

/**
 * A function to convert w3w string to lat/lng data
 * @param {string} w3word - W3w to convert
 * @param {string} lang - Language for conversion
 * @returns {object}  Return a lat/lng object
 */
export const getCoordinatesByW3wInfo = async (w3word, lang = "it") => {
  const response = await window.what3words.api.convertToCoordinates({
    words: w3word,
    language: lang,
    format: "json", // or format: 'geojson'
  });

  return response;
};

/**
 * A function to get a list of stories with a radius search approach
 * @param {string} lat - Latitude position
 * @param {string} lng - Longitude position
 * @param {number} radius - Radius value
 * @returns {object}  Return an object with a list of stories or an error object
 */
export const getCurrentMarkersList = async (lat, lng, radius = 20000) => {
  //call remote api to retrieve list of current markers sending a radial
  //seach centered in lat, lang and radix=5km

  try {
    const response = await fetch(
      url_backend + "offers/" + lat + "/" + lng + "/" + radius,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        method: "GET",
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || response.statusText);
    }
    const json = await response.json();
    const success = json.errorResponse ? false : true;
    return { success, data: json };
  } catch (error) {
    console.error(
      "Si è verificato un errore durante l'ottenimento di un marker:",
      error
    );
    return { success: false };
  }
};

/**
 * A function to get a list of stories filtered by category
 * @param {string} category - Category for filtering
 * @param {number} radius - Max radius to search
 * @returns {object}  Return an object with a list of stories or an error object
 */
export const getCurrentMarkersCategoryList = async (category, radius) => {
  try {
    const response = await fetch(
      url_backend + "offers/searchcategory/" + category + "/20000",
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        method: "GET",
        // …
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || response.statusText);
    }
    const json = await response.json();
    const success = json.errorResponse ? false : true;
    return { success, data: json };
  } catch (error) {
    console.error(
      "Si è verificato un errore durante l'ottenimento dei marker filtrati per categoria:",
      error
    );
    return { success: false };
  }
};

/**
 * A function to get the list of your stories
 * @returns {object}  Return a list of stories
 */
export const getOwnershipMarkersList = async () => {
  try {
    const response = await fetch(url_backend + "offers/ownership", {
      headers: {
        "Access-Control-Allow-Credentials": "true",
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      method: "GET",
      // …options
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || response.statusText);
    }
    const json = await response.json();
    const success = json.errorResponse ? false : true;
    return { success, data: json };
  } catch (error) {
    console.error(
      "Si è verificato un errore durante l'ottenimento delle tue storie:",
      error
    );
    return { success: false };
  }
};

/**
 * A function to call remote api to retrieve the info of a specific story identified by id
 * @param {string} id - A story id
 * @returns {object}  Return a story object or an error object
 */
export const getCurrentMarkerObj = async (id) => {
  try {
    const response = await fetch(url_backend + "offers/" + id, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "GET",
      // …
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || response.statusText);
    }
    const json = await response.json();
    const success = json.errorResponse ? false : true;
    return { success, data: json };
  } catch (error) {
    console.error(
      "Si è verificato un errore durante l'ottenimento di un marker:",
      error
    );
    return { success: false };
  }
};

/**
 * A function to remove a story image
 * @param {string} storyID - Story identifier
 * @param {string} imageID - Image identifier
 * @returns {object}  Return a story object or an error object
 */
export const removePhotoCurrentMarkerObj = async (storyID, imageID) => {
  //call remote api to save the info of a specific marker identified by id
  try {
    const response = await fetch(
      url_backend + "offers/" + storyID + "/removephoto",
      {
        headers: {
          "Access-Control-Allow-Credentials": "true",
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
        method: "PATCH",
        body: JSON.stringify({ id: imageID }),
        // …
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || response.statusText);
    }
    const json = await response.json();
    const success = json.errorResponse ? false : true;
    return { success, data: json };
  } catch (error) {
    console.error(
      "Si è verificato un errore durante l'eliminazione di una foto di un marker:",
      error
    );
    return { success: false };
  }
};

/**
 * A function to decode base64 data
 * @param {object} obj - Story object to update
 * @returns {object}  Return a story object or an error object
 */
export const updateCurrentMarkerObj = async (obj) => {
  try {
    if (!isValidMarkerId(obj.id)) {
      const errorMessage = "Id del marker non valido!";
      throw new Error(errorMessage);
    }
    //call remote api to save the info of a specific story identified by id
    const response = await fetch(url_backend + "offers/" + obj.id, {
      headers: {
        "Access-Control-Allow-Credentials": "true",
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      method: "PUT",
      body: JSON.stringify({
        descriptionGive: obj.description ? obj.description : "",
        status: "ACTIVE",
        photos: obj.photos ? obj.photos : undefined,
        name: obj.name ? obj.name : "",
        category: obj.category ? obj.category : "",
        position: {
          type: "Point",
          coordinates: [obj.position.lng, obj.position.lat],
        },
        w3wData: {
          address: obj.w3wData.address,
          idW: obj.w3wData.idW,
          _id: obj.w3wData._id ? obj.w3wData._id : undefined,
        },
      }),
      // …
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || response.statusText);
    }

    const json = await response.json();
    const success = json.errorResponse ? false : true;
    return { success, data: json };
  } catch (error) {
    console.error(
      "Si è verificato un errore durante l'aggiornamento di un marker:",
      error
    );
    return { success: false };
  }
};

/**
 * A function to validate a w3w object
 * @param {object} w3wObject - W3w object representation to check
 * @returns {boolean}  Return true/false if w3w object is valid/not valid
 */
export const isValidW3W = (w3wObject) => {
  if (!w3wObject) {
    console.log("!!!!!--- w3w object undefined!");
    return false;
  }
  if (
    !w3wObject.hasOwnProperty("address") ||
    !w3wObject.hasOwnProperty("idW")
  ) {
    console.log("!!!!!--- w3w object property address or idW  undefined!");
    return false;
  }

  if (w3wObject.address.trim().length < 4 || w3wObject.idW.trim().length < 4) {
    console.log("!!!!!--- w3w object property address or idW  not valid!");
    return false;
  }

  return true;
};

/**
 * A function to validate a lat/long object
 * @param {object} positionObject - Lat/Lng object to check
 * @returns {object}  Return true/false if lat/lng object is valid/not valid
 */
export const isValidPosition = (positionObject) => {
  if (!positionObject) {
    console.log("!!!!!--- position object undefined!");
    return false;
  }

  if (
    !positionObject.hasOwnProperty("lng") ||
    !positionObject.hasOwnProperty("lat")
  ) {
    console.log(
      "!!!!!--- position object property position with lng or lat  undefined!"
    );
    return false;
  }

  return true;
};

/**
 * A function to create a new story
 * @param {object} obj - A story object representation
 * @returns {object}  Return a story object or an error object
 */
export const createMarkerObj = async (obj) => {
  //create a new marker
  if (!isValidW3W(obj.w3wData) || !isValidPosition(obj.position)) {
    console.log("position lat/lng or w3w required before save a marker");
    return false;
  }

  try {
    const response = await fetch(url_backend + "offers", {
      headers: {
        "Access-Control-Allow-Credentials": "true",
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      method: "POST",
      body: JSON.stringify({
        descriptionGive: obj.description ? obj.description : "",
        status: "ACTIVE",
        photos: obj.photos ? obj.photos : [],
        name: obj.name ? obj.name : "",
        category: obj.category ? obj.category : "",
        position: {
          type: "Point",
          coordinates: [obj.position.lng, obj.position.lat],
        },
        w3wData: {
          address: obj.w3wData.address,
          idW: obj.w3wData.idW,
        },
      }),
      // …
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || response.statusText);
    }
    const json = await response.json();
    const success = json.errorResponse ? false : true;
    return { success, data: json };
  } catch (error) {
    console.error(
      "Si è verificato un errore durante la creazione di un marker:",
      error
    );
    return { success: false };
  }
};

/**
 * A function to send an image to the cloud
 * @param {object} objImage - Object image representation
 * @returns {object}  Return an image data
 */
export const uploadImage = async (objImage) => {
  try {
    const url = `https://api.cloudinary.com/v1_1/inventu/upload`;
    const fd = new FormData();
    fd.append("upload_preset", "lewis_");
    fd.append("folder", "lewis");
    fd.append("tags", "lewis_story"); // Optional - add tags for image admin in Cloudinary
    fd.append("file", objImage.file);

    const response = await fetch(url, {
      method: "POST",
      body: fd,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        `Errore durante l'upload: ${error.message || response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(
      "Si è verificato un errore durante l'upload dell'immagine:",
      error
    );
    return { success: false, error: error.message };
  }
};

/**
 * A function to add a watermarker text to the image
 * @param {string} url - Image url
 * @param {string} text - Watermark text
 * @returns {object}  Return a modified url
 */
export const addWatermarkText = (url, text) => {
  const urlParametersText =
    "y_10,x_10,b_white,l_text:Arial_100_bold:" +
    encodeURIComponent("%2F%2F%2F%20") +
    text +
    "/w_0.3,c_fit,fl_relative,fl_layer_apply,g_north";

  const urlModified = url.replace("/upload/", `/upload/${urlParametersText}/`);
  return urlModified;
};

/**
 * A function to delete an image
 * @param {string} publicId - An image Id
 * @returns {object}  Return a deleted cloud image
 */
export const deleteImage = async (publicId) => {
  const cloudName = "inventu"; // Sostituisci con il tuo Cloud Name di Cloudinary
  const apiKey = "735529667548655"; // Sostituisci con la tua API Key di Cloudinary
  const apiSecret = "gmtk1Fi_vrN236THzvJZwTNOUcQ"; // Sostituisci con la tua API Secret di Cloudinary

  const timestamp = Math.floor(Date.now() / 1000);
  const signature = CryptoJS.SHA1(
    `public_id=${publicId}&timestamp=${timestamp}${apiSecret}`
  ).toString();

  const url = `https://api.cloudinary.com/v1_1/${cloudName}/image/destroy`;

  const formData = new FormData();
  formData.append("public_id", publicId);
  formData.append("timestamp", timestamp);
  formData.append("signature", signature);
  formData.append("api_key", apiKey);

  try {
    const response = await fetch(url, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      console.error("Errore durante la cancellazione dell'immagine:", error);
      return { success: false, error: error };
    }

    const data = await response.json();
    console.log("Immagine cancellata con successo:", data);
    return { success: true, data: data };
  } catch (error) {
    console.error("Errore durante la chiamata fetch:", error);
    return { success: false, error: error.message };
  }
};

/**
 * A function to delete a story by ID
 * @param {string} id - Story ID
 * @returns {object}  Return a deleted story
 */
export const deleteMarkerById = async (id) => {
  //call remote api to save the info of a specific marker identified by id
  try {
    if (!isValidMarkerId(id)) {
      const errorMessage = "Id del marker non valido!";
      throw new Error(errorMessage);
    }
    const response = await fetch(url_backend + "offers/" + id, {
      headers: {
        "Access-Control-Allow-Credentials": "true",
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      method: "DELETE",
      // …
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || response.statusText);
    }

    const json = await response.json();
    const success = json.errorResponse ? false : true;
    return { success, data: json };
  } catch (error) {
    console.error(
      "Si è verificato un errore durante l'aggiornamento di un marker:",
      error
    );
    return { success: false, error };
  }
};

/**
 * A function to validate a story ID
 * @param {string} id - A story ID
 * @returns {boolean}   Return true/false if ID is valid/not valid
 */
export const isValidMarkerId = (id) => {
  if (typeof id !== "undefined") {
    return true;
  }
  return false;
};

/**
 * A function to validate a story ID and check if different from another
 * @param {string} id - A story ID
 * @param {string} optionID - A story ID
 * @returns {boolean}   Return true/false if ID is valid/not valid and different from another
 */
export const isNotValidMarkerIdAndDifferent = (dataID, optionID) => {
  if (typeof dataID === "undefined") {
    return true;
  }
  if (dataID !== optionID) {
    return true;
  }

  return false;
};

/**
 * A function to get a restricted list of categories
 * @returns {String[]}  Return an array of categories
 */
export const getCategoriesForSaving = () => {
  return [
    "Scegli categoria",
    "Tourist attraction", // Opzione di default, usata come placeholder
    "Hotel",
    "Restaurant",
    "Bed & breakfast",
    "Spa",
    "Marina",
    "Museum",
    "Archaeological site",
    "Catholic church",
  ];
};

/**
 * A function to get a translated name category
 * @returns {string}  Return a translated category
 */
export const getTranslation = (key) => {
  const capitalized = key.charAt(0).toUpperCase() + key.slice(1);
  const translation = {
    Monday: t`Monday`,
    Tuesday: t`Tuesday`,
    Wednesday: t`Wednesday`,
    Thursday: t`Thursday`,
    Friday: t`Friday`,
    Saturday: t`Saturday`,
    Sunday: t`Sunday`,
    Dismiss: t`Dismiss`,
    Closed: t`Closed`,
    "Adventure sports center": t`Adventure sports center`,
    "Agricultural organization": t`Agricultural organization`,
    "Agricultural service": t`Agricultural service`,
    "Airport parking lot": t`Airport parking lot`,
    Amphitheater: t`Amphitheater`,
    "Amusement center": t`Amusement center`,
    "Amusement park": t`Amusement park`,
    "Amusement park ride": t`Amusement park ride`,
    "Animal protection organization": t`Animal protection organization`,
    "Animal shelter": t`Animal shelter`,
    "Apartment complex": t`Apartment complex`,
    "Apartment rental agency": t`Apartment rental agency`,
    "Archaeological museum": t`Archaeological museum`,
    "Archaeological site": t`Archaeological site`,
    "Army museum": t`Army museum`,
    "Art center": t`Art center`,
    "Art museum": t`Art museum`,
    "Art studio": t`Art studio`,
    "Arts organization": t`Arts organization`,
    "Assisted living facility": t`Assisted living facility`,
    "Association / Organization": t`Association / Organization`,
    "Auto broker": t`Auto broker`,
    "Auto repair shop": t`Auto repair shop`,
    Bank: t`Bank`,
    Bar: t`Bar`,
    "Barbecue restaurant": t`Barbecue restaurant`,
    Basilica: t`Basilica`,
    Beach: t`Beach`,
    "Beauty salon": t`Beauty salon`,
    "Bed & breakfast": t`Bed & breakfast`,
    "Bicycle rental service": t`Bicycle rental service`,
    Bistro: t`Bistro`,
    "Boat rental service": t`Boat rental service`,
    "Boat storage facility": t`Boat storage facility`,
    "Boat tour agency": t`Boat tour agency`,
    "Botanical garden": t`Botanical garden`,
    "Building firm": t`Building firm`,
    "Business to business service": t`Business to business service`,
    Campground: t`Campground`,
    "Camping cabin": t`Camping cabin`,
    "Camping farm": t`Camping farm`,
    "Canoe & kayak rental service": t`Canoe & kayak rental service`,
    "Canoe & kayak tour agency": t`Canoe & kayak tour agency`,
    "Canoe and kayak club": t`Canoe and kayak club`,
    "Car racing venue": t`Car racing venue`,
    "Carabinieri police": t`Carabinieri police`,
    Castle: t`Castle`,
    Cathedral: t`Cathedral`,
    "Catholic church": t`Catholic church`,
    Cemetery: t`Cemetery`,
    "Cheese shop": t`Cheese shop`,
    "Children's camp": t`Children's camp`,
    "City government office": t`City government office`,
    "City or town hall": t`City or town hall`,
    "City park": t`City park`,
    "Coast guard station": t`Coast guard station`,
    "Community center": t`Community center`,
    "Community garden": t`Community garden`,
    "Construction equipment supplier": t`Construction equipment supplier`,
    Consultant: t`Consultant`,
    Cooperative: t`Cooperative`,
    "Corporate office": t`Corporate office`,
    Council: t`Council`,
    "Country food restaurant": t`Country food restaurant`,
    "Country park": t`Country park`,
    "Courier service": t`Courier service`,
    "Craft store": t`Craft store`,
    "Cultural center": t`Cultural center`,
    "Cultural landmark": t`Cultural landmark`,
    "Curtain supplier and maker": t`Curtain supplier and maker`,
    Dairy: t`Dairy`,
    "Dance club": t`Dance club`,
    Diner: t`Diner`,
    "Disco club": t`Disco club`,
    Distillery: t`Distillery`,
    "Diving center": t`Diving center`,
    "Dog park": t`Dog park`,
    "Drilling contractor": t`Drilling contractor`,
    "Driver's license office": t`Driver's license office`,
    "E commerce agency": t`E commerce agency`,
    "Ecological park": t`Ecological park`,
    "Education center": t`Education center`,
    "Educational institution": t`Educational institution`,
    "Electric utility company": t`Electric utility company`,
    "Equestrian facility": t`Equestrian facility`,
    "Espresso bar": t`Espresso bar`,
    "Ethnographic museum": t`Ethnographic museum`,
    "Excavating contractor": t`Excavating contractor`,
    Exhibit: t`Exhibit`,
    "Extended stay hotel": t`Extended stay hotel`,
    Farm: t`Farm`,
    "Farm school": t`Farm school`,
    Farmstay: t`Farmstay`,
    "Fine dining restaurant": t`Fine dining restaurant`,
    "Fishing charter": t`Fishing charter`,
    Fortress: t`Fortress`,
    "Free parking lot": t`Free parking lot`,
    Garden: t`Garden`,
    Gardener: t`Gardener`,
    "Gas station": t`Gas station`,
    Gastropub: t`Gastropub`,
    "General education school": t`General education school`,
    "Go-karting venue": t`Go-karting venue`,
    "Golf club": t`Golf club`,
    "Government hospital": t`Government hospital`,
    "Government office": t`Government office`,
    "Green energy supplier": t`Green energy supplier`,
    "Guest house": t`Guest house`,
    "Health spa": t`Health spa`,
    Heliport: t`Heliport`,
    "Heritage museum": t`Heritage museum`,
    "Hiking area": t`Hiking area`,
    "Historical landmark": t`Historical landmark`,
    "History museum": t`History museum`,
    "Holiday apartment": t`Holiday apartment`,
    "Holiday apartment rental": t`Holiday apartment rental`,
    "Holiday home": t`Holiday home`,
    "Holiday park": t`Holiday park`,
    "Horse breeder": t`Horse breeder`,
    "Horse riding school": t`Horse riding school`,
    "Horseback riding service": t`Horseback riding service`,
    Hostel: t`Hostel`,
    Hotel: t`Hotel`,
    "Housing development": t`Housing development`,
    "Ice cream shop": t`Ice cream shop`,
    "Indoor lodging": t`Indoor lodging`,
    Inn: t`Inn`,
    "Italian grocery store": t`Italian grocery store`,
    "Italian restaurant": t`Italian restaurant`,
    Kennel: t`Kennel`,
    Lake: t`Lake`,
    "Land surveying office": t`Land surveying office`,
    Library: t`Library`,
    Lido: t`Lido`,
    "Local government office": t`Local government office`,
    "Local history museum": t`Local history museum`,
    Lodge: t`Lodge`,
    Lodging: t`Lodging`,
    "Machine workshop": t`Machine workshop`,
    "Marble contractor": t`Marble contractor`,
    Marina: t`Marina`,
    Market: t`Market`,
    "Medical clinic": t`Medical clinic`,
    "Mediterranean restaurant": t`Mediterranean restaurant`,
    "Military airport": t`Military airport`,
    Mine: t`Mine`,
    "Mineral water company": t`Mineral water company`,
    "Mobile home park": t`Mobile home park`,
    "Mountain cabin": t`Mountain cabin`,
    "Movie theater": t`Movie theater`,
    Museum: t`Museum`,
    "National forest": t`National forest`,
    "National park": t`National park`,
    "Nature preserve": t`Nature preserve`,
    "News service": t`News service`,
    "Non-profit organization": t`Non-profit organization`,
    "Nursing home": t`Nursing home`,
    "Observation deck": t`Observation deck`,
    "Olive oil manufacturer": t`Olive oil manufacturer`,
    "Open air museum": t`Open air museum`,
    "Organic farm": t`Organic farm`,
    "Outdoor activity organiser": t`Outdoor activity organiser`,
    "Outdoor clothing and equipment shop": t`Outdoor clothing and equipment shop`,
    "Outdoor swimming pool": t`Outdoor swimming pool`,
    Park: t`Park`,
    "Park & ride": t`Park & ride`,
    "Parking garage": t`Parking garage`,
    "Parking lot": t`Parking lot`,
    "Pet adoption service": t`Pet adoption service`,
    "Pet boarding service": t`Pet boarding service`,
    "Phone repair service": t`Phone repair service`,
    "Photo lab": t`Photo lab`,
    "Picnic ground": t`Picnic ground`,
    "Pizza restaurant": t`Pizza restaurant`,
    "Place of worship": t`Place of worship`,
    "Plumbing supply store": t`Plumbing supply store`,
    "Port authority": t`Port authority`,
    "Post office": t`Post office`,
    "Power station": t`Power station`,
    Prison: t`Prison`,
    "Private hospital": t`Private hospital`,
    Pub: t`Pub`,
    "Public beach": t`Public beach`,
    "Public library": t`Public library`,
    "Public parking space": t`Public parking space`,
    Ranch: t`Ranch`,
    "Real estate agency": t`Real estate agency`,
    "Records storage facility": t`Records storage facility`,
    "Rehabilitation center": t`Rehabilitation center`,
    "Religious destination": t`Religious destination`,
    "Religious organization": t`Religious organization`,
    "Research institute": t`Research institute`,
    "Residents association": t`Residents association`,
    "Resort hotel": t`Resort hotel`,
    "Rest stop": t`Rest stop`,
    Restaurant: t`Restaurant`,
    "Retirement home": t`Retirement home`,
    "Retreat center": t`Retreat center`,
    "Rock climbing": t`Rock climbing`,
    "Roofing contractor": t`Roofing contractor`,
    "Roofing supply store": t`Roofing supply store`,
    "RV park": t`RV park`,
    "Sardinian restaurant": t`Sardinian restaurant`,
    "Scenic spot": t`Scenic spot`,
    "Science museum": t`Science museum`,
    "Scooter rental service": t`Scooter rental service`,
    "Screen printing shop": t`Screen printing shop`,
    Sculpture: t`Sculpture`,
    "Seafood restaurant": t`Seafood restaurant`,
    "Septic system service": t`Septic system service`,
    "Serviced accommodation": t`Serviced accommodation`,
    Shelter: t`Shelter`,
    "Shipping and mailing service": t`Shipping and mailing service`,
    "Shipping service": t`Shipping service`,
    "Shopping mall": t`Shopping mall`,
    Shrine: t`Shrine`,
    "Sightseeing tour agency": t`Sightseeing tour agency`,
    Spa: t`Spa`,
    "Spa and health club": t`Spa and health club`,
    "Sporting goods store": t`Sporting goods store`,
    Stadium: t`Stadium`,
    "State government office": t`State government office`,
    "State park": t`State park`,
    "Steak house": t`Steak house`,
    "Subway station": t`Subway station`,
    Supermarket: t`Supermarket`,
    "Surf school": t`Surf school`,
    "Swimming pool": t`Swimming pool`,
    "Theme park": t`Theme park`,
    "Thermal baths": t`Thermal baths`,
    "Tour agency": t`Tour agency`,
    "Tour operator": t`Tour operator`,
    "Tourist attraction": t`Tourist attraction`,
    "Tourist information center": t`Tourist information center`,
    "Transportation service": t`Transportation service`,
    "Tsukigime parking lot": t`Tsukigime parking lot`,
    type: t`type`,
    "Vacation home rental agency": t`Vacation home rental agency`,
    Villa: t`Villa`,
    Vineyard: t`Vineyard`,
    "Visitor center": t`Visitor center`,
    "Volunteer organization": t`Volunteer organization`,
    "Waste management service": t`Waste management service`,
    "Weather forecast service": t`Weather forecast service`,
    "Wedding venue": t`Wedding venue`,
    Weir: t`Weir`,
    "Wellness center": t`Wellness center`,
    "Wellness hotel": t`Wellness hotel`,
    "Wildlife and safari park": t`Wildlife and safari park`,
    "Window supplier": t`Window supplier`,
    "Wine bar": t`Wine bar`,
    Winery: t`Winery`,
    Zoo: t`Zoo`,
  };
  return translation[capitalized] ? translation[capitalized] : capitalized;
};
